/* uploadDemoVideo — chunked campaign-video upload for system admins.
   Slices the file into 3MB chunks (base64) to stay under the ingress
   body limit, then finalizes. Progress callback gets 0-100. */
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const CHUNK_BYTES = 3 * 1024 * 1024;
export const VIDEO_MAX_BYTES = 60 * 1024 * 1024;

function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const s = reader.result || "";
      const comma = s.indexOf(",");
      resolve(comma >= 0 ? s.slice(comma + 1) : s);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

export async function uploadDemoVideo(file, onProgress) {
  const init = await axios.post(
    `${API}/admin/demo-video/upload/init`,
    { filename: file.name, mime_type: file.type || "video/mp4", size_bytes: file.size },
    { withCredentials: true },
  );
  const uploadId = init.data.upload_id;
  const totalChunks = Math.ceil(file.size / CHUNK_BYTES);
  for (let i = 0; i < totalChunks; i++) {
    const slice = file.slice(i * CHUNK_BYTES, Math.min(file.size, (i + 1) * CHUNK_BYTES));
    const data_b64 = await blobToBase64(slice);
    await axios.post(
      `${API}/admin/demo-video/upload/${uploadId}/chunk`,
      { index: i, data_b64 },
      { withCredentials: true },
    );
    onProgress?.(Math.round(((i + 1) / (totalChunks + 1)) * 100));
  }
  const done = await axios.post(
    `${API}/admin/demo-video/upload/${uploadId}/complete`,
    {},
    { withCredentials: true },
  );
  onProgress?.(100);
  return done.data;
}

/* Frame-grabs the video ~1s in and cover-crops to 1200×630 (the OG
   image standard). Runs entirely in the admin's browser — the server
   has no ffmpeg. Resolves to a base64 JPEG (no data-URL prefix). */
export function captureVideoThumbnail(file, { width = 1200, height = 630 } = {}) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement("video");
    let settled = false;
    const finish = (fn, arg) => {
      if (settled) return;
      settled = true;
      try { URL.revokeObjectURL(url); } catch { /* noop */ }
      fn(arg);
    };
    video.muted = true;
    video.playsInline = true;
    video.preload = "auto";
    video.onloadedmetadata = () => {
      video.currentTime = Math.min(1.0, Math.max(0.1, (video.duration || 2) / 2));
    };
    video.onseeked = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        const vw = video.videoWidth, vh = video.videoHeight;
        if (!vw || !vh) throw new Error("no video dimensions");
        const scale = Math.max(width / vw, height / vh);
        const dw = vw * scale, dh = vh * scale;
        ctx.drawImage(video, (width - dw) / 2, (height - dh) / 2, dw, dh);
        canvas.toBlob((blob) => {
          if (!blob) return finish(reject, new Error("thumbnail encode failed"));
          blobToBase64(blob).then((b64) => finish(resolve, b64)).catch((e) => finish(reject, e));
        }, "image/jpeg", 0.85);
      } catch (e) {
        finish(reject, e);
      }
    };
    video.onerror = () => finish(reject, new Error("could not decode video"));
    setTimeout(() => finish(reject, new Error("thumbnail capture timed out")), 15000);
    video.src = url;
  });
}

export async function uploadDemoThumbnail(imageB64, mime = "image/jpeg") {
  await axios.post(
    `${API}/admin/demo-video/thumbnail`,
    { image_b64: imageB64, mime_type: mime },
    { withCredentials: true },
  );
}
